// src/components/PlannerProductCard.js
import React, { useState, useEffect } from "react"
import { IoClose, IoDuplicateSharp } from "react-icons/io5"
import placeholder from "../assets/placeholder.jpg"

// Helper function to detect if a hex color is light (needs dark border)
const isLightColor = (hexColor) => {
  if (!hexColor || !hexColor.startsWith("#")) return false
  const hex = hexColor.replace("#", "")
  const fullHex =
    hex.length === 3
      ? hex
          .split("")
          .map((c) => c + c)
          .join("")
      : hex
  const r = parseInt(fullHex.substring(0, 2), 16)
  const g = parseInt(fullHex.substring(2, 4), 16)
  const b = parseInt(fullHex.substring(4, 6), 16)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luminance > 0.85
}

// Fetch high-res preview.png from Emersya showcase page
const fetchHighResPreview = async (productCode) => {
  try {
    const response = await fetch(
      `https://emersya.com/jsShowcase/${productCode}?container=emersyaIframe`,
    )
    if (!response.ok) return null
    const data = await response.text()
    const match = data.match(
      /src="(https:\/\/cdn\.emersya\.com\/cl\/[^"]+\/preview\.png\?date=[^"]+)"/,
    )
    return match ? match[1] : null
  } catch {
    return null
  }
}

// Cache for fetched high-res previews
const previewCache = {}

/**
 * Collapsed product card for the planner grid view
 * Shows product image, name, EDIT button, and material preview swatches
 * Material thumbnails match MaterialTabs styling
 */
const PlannerProductCard = ({
  pn,
  onEdit,
  removeProductNodeInstance,
  duplicateProductNode,
  language = "it",
  isActive = false,
}) => {
  const productName =
    pn.matchedLocalProduct?.name || pn.refData?.name || "Prodotto"
  const productPrice =
    pn.refData?.price || pn.matchedLocalProduct?.price || null
  const [imgSrc, setImgSrc] = useState(pn.refData?.previewPath || placeholder)

  // Update imgSrc when refData changes (e.g. after product matching)
  useEffect(() => {
    const path = pn.refData?.previewPath
    if (path && !path.includes("/productNodes/")) {
      setImgSrc(path)
    }
  }, [pn.refData?.previewPath])

  // Try to fetch high-res preview if current image is low-res or missing
  useEffect(() => {
    const currentPath = pn.refData?.previewPath || ""
    // The showcase code is needed for fetching preview.png (different from projectCode)
    const showcaseCode =
      pn.matchedLocalProduct?.code || pn.refData?.code || pn.code
    const cacheKey = showcaseCode || pn.projectCode

    if (!cacheKey) return

    // Check if already cached
    if (previewCache[cacheKey]) {
      setImgSrc(previewCache[cacheKey])
      return
    }

    // If no preview, uses fallback productNodes/86003, or is a productNodes URL (low-res)
    const isLowRes =
      !currentPath ||
      currentPath.includes("productNodes/86003") ||
      (currentPath.includes("/productNodes/") &&
        !currentPath.includes("preview.png"))

    if (isLowRes && showcaseCode) {
      fetchHighResPreview(showcaseCode).then((highRes) => {
        if (highRes) {
          previewCache[cacheKey] = highRes
          setImgSrc(highRes)
          // Also update refData so export saves the good URL
          if (pn.refData) {
            pn.refData.previewPath = highRes
          }
        }
      })
    }
  }, [
    pn.refData?.previewPath,
    pn.refData?.code,
    pn.matchedLocalProduct?.code,
    pn.projectCode,
  ])

  // Get up to 3 material previews for the card
  const getMaterialPreviews = () => {
    if (!pn.configurableMaterials) return []

    return pn.configurableMaterials
      .filter(
        (mat) => mat.materialVariations && mat.materialVariations.length > 1,
      )
      .slice(0, 3)
      .map((mat) => {
        const variationName = mat.appliedVariation || mat.activeVariation
        const variation = mat.materialVariations?.find(
          (v) => v.name === variationName,
        )
        return {
          name: mat.name,
          thumbnail: variation?.thumbnail || variation?.color || "#ccc",
        }
      })
  }

  const materialPreviews = getMaterialPreviews()

  return (
    <div
      className={`ay-bg-gray-100 ay-rounded-2xl ay-overflow-hidden ay-relative ay-group ay-cursor-pointer ay-h-full ay-transition-shadow ay-duration-200 ${
        isActive
          ? "ay-ring-2 ay-ring-black"
          : pn.colliding
            ? "ay-ring-2 ay-ring-red-500"
            : ""
      }`}
      onClick={onEdit}
    >
      {/* Action Buttons - Top Right, visible on hover */}
      <div className="ay-absolute ay-top-2 ay-right-2 ay-z-10 ay-flex ay-gap-1 ay-opacity-0 group-hover:ay-opacity-100 ay-transition-opacity ay-duration-200">
        <button
          onClick={(e) => {
            e.stopPropagation()
            duplicateProductNode(pn)(e)
          }}
          className="ay-w-7 ay-h-7 ay-flex ay-items-center ay-justify-center ay-bg-white ay-rounded-full ay-shadow-md ay-text-gray-600 hover:ay-text-blue-600 hover:ay-bg-blue-50 ay-transition-colors"
          title="Duplica"
        >
          <IoDuplicateSharp size={14} />
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation()
            removeProductNodeInstance(pn)(e)
          }}
          className="ay-w-7 ay-h-7 ay-flex ay-items-center ay-justify-center ay-bg-white ay-rounded-full ay-shadow-md ay-text-gray-600 hover:ay-text-red-600 hover:ay-bg-red-50 ay-transition-colors"
          title="Rimuovi"
        >
          <IoClose size={16} />
        </button>
      </div>

      {/* Product Image */}
      <div className="ay-relative ay-aspect-square ay-overflow-hidden">
        <img
          src={imgSrc}
          alt={productName}
          className="ay-w-full ay-h-full ay-object-cover"
          onError={(e) => {
            e.target.src = placeholder
          }}
        />
      </div>

      {/* Product Info */}
      <div className="ay-p-2">
        <div className="ay-flex ay-items-start ay-justify-between ay-mb-1.5">
          <div className="ay-flex-1 ay-min-w-0">
            <h3
              className="ay-font-sans ay-font-medium ay-text-xs ay-text-gray-900 ay-truncate"
              title={productName}
            >
              {productName}
            </h3>
            {productPrice && (
              <span className="ay-text-[10px] ay-font-sans ay-font-semibold ay-text-gray-500">
                {productPrice.toLocaleString("it-IT", {
                  minimumFractionDigits: 2,
                })}{" "}
                EUR
              </span>
            )}
          </div>
          <span className="ay-text-[8px] ay-font-serif ay-font-bold ay-text-gray-400 ay-uppercase ay-tracking-wider ay-ml-1 ay-mt-0.5 ay-flex-shrink-0 group-hover:ay-text-gray-600 ay-transition-colors">
            EDIT
          </span>
        </div>

        {/* Material Preview Swatches - MaterialTabs style */}
        {materialPreviews.length > 0 && (
          <div className="ay-flex ay-gap-1">
            {materialPreviews.map((mat, idx) => {
              const needsDarkBorder = isLightColor(mat.thumbnail)
              return (
                <div
                  key={idx}
                  className="ay-group/mat ay-w-6 ay-h-6 ay-relative"
                  title={mat.name}
                >
                  <div
                    className="ay-absolute ay-inset-[0%] ay-overflow-hidden ay-transition-[inset] ay-duration-300 ay-ease-out group-hover/mat:ay-inset-[8%]"
                    style={{
                      border: needsDarkBorder ? "1px solid #000" : "none",
                    }}
                  >
                    {mat.thumbnail.startsWith("#") ? (
                      <div
                        className="ay-w-full ay-h-full"
                        style={{ backgroundColor: mat.thumbnail }}
                      />
                    ) : (
                      <img
                        src={mat.thumbnail}
                        alt={mat.name}
                        className="ay-w-full ay-h-full ay-object-cover ay-rotate-90"
                        onError={(e) => {
                          e.target.style.backgroundColor = "#ccc"
                          e.target.src = ""
                        }}
                      />
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

export default PlannerProductCard
